import { Fragment, useContext } from 'react'
import { Table, Thead, Tbody, Tr, Th, Td, Link, Text, Divider, Flex, HStack, Skeleton } from '@chakra-ui/react'
import { BiUpvote, BiDownvote } from 'react-icons/bi'
import { LoadingCtx } from './LoadingCtxAI.jsx'
import { entryUrl } from './donaHelpersAI.jsx'

const SkeletonRows = ({count = 5}) => {
  const colCount = 4
  return Array.from({length: count}, (_, idx) => (
    <Tr key={idx}>
      <Td fontWeight="bold" borderBottom="none">{idx + 1}</Td>
      <Td borderBottom="none"><Skeleton h="1.2em" w="120px" /></Td>
      <Td isNumeric borderBottom="none"><Skeleton h="1.2em" w="60px" ml="auto" /></Td>
      <Td borderBottom="none" p={1}>
        <HStack spacing={1}>
          <BiUpvote size={20} color="var(--chakra-colors-gray-300)" />
          <BiDownvote size={20} color="var(--chakra-colors-gray-300)" />
        </HStack>
      </Td>
    </Tr>
  ))
}

export const VotingTable = ({ entries, myVotes, emptyBuyUrl = '/donate?amount=1.00' }) => {
  const {loading} = useContext(LoadingCtx)
  const colCount = 4

  if (loading) return (
    <Table variant="simple">
      <Thead>
        <Tr>
          <Th w="50px" borderBottom="none">#</Th>
          <Th borderBottom="none">String</Th>
          <Th isNumeric w="100px" borderBottom="none">Amount</Th>
          <Th w="80px" borderBottom="none"></Th>
        </Tr>
      </Thead>
      <Tbody>
        <SkeletonRows />
      </Tbody>
    </Table>
  )

  return (
    <Table variant="simple">
      <Thead>
        <Tr>
          <Th w="50px" borderBottom="none">#</Th>
          <Th borderBottom="none">String</Th>
          <Th isNumeric w="100px" borderBottom="none">Amount</Th>
          <Th w="80px" borderBottom="none"></Th>
        </Tr>
      </Thead>
      <Tbody>
        {entries.map((entry, idx) => {
          const displayAmount = entry.netUsd ?? entry.amountUsd
          const buyPrice = (Math.abs(displayAmount) + 0.01).toFixed(2)
          const prevAmount = idx > 0 ? (entries[idx - 1].netUsd ?? entries[idx - 1].amountUsd) : null
          const prevBuyPrice = prevAmount !== null ? (Math.abs(prevAmount) + 0.01).toFixed(2) : null
          const showBuy = buyPrice !== prevBuyPrice

          const myVote = myVotes?.get(entry.donaRef)
          const buyUrl = `/donate?amount=${buyPrice}`
          const supportUrl = `/donate?chipIn=${entry.id}`
          const opposeUrl = `/donate?chipIn=${entry.id}&oppose=1`
          const detailUrl = entryUrl(entry.id)

          return (
            <Fragment key={entry.id}>
              <Tr>
                <Td colSpan={colCount} p={0} borderBottom="none">
                  <Flex align="center">
                    <Divider flex={1} />
                    {showBuy && (
                      <>
                        <Link href={buyUrl} color="gray.400" fontSize="xs" px={2} _hover={{color: 'blue.500'}}>
                          buy this spot for ${buyPrice}
                        </Link>
                        <Divider flex={1} />
                      </>
                    )}
                  </Flex>
                </Td>
              </Tr>
              <Tr>
                <Td fontWeight="bold" borderBottom="none">{idx + 1}</Td>
                <Td borderBottom="none">
                  <Link href={detailUrl} color="blue.600" fontWeight="bold">
                    {entry.displayName ?? entry.donaRef}
                  </Link>
                </Td>
                <Td isNumeric borderBottom="none">
                  <Text color={displayAmount < 0 ? 'red.500' : undefined}>${displayAmount.toFixed(2)}</Text>
                </Td>
                <Td borderBottom="none" p={1}>
                  <HStack spacing={1}>
                    <Link href={supportUrl} color="green.400" _hover={{color: 'green.600'}}>
                      <BiUpvote size={20} style={{fill: myVote?.stance === 'support' ? 'currentColor' : undefined, strokeWidth: myVote?.stance === 'support' ? 2 : 0}} />
                    </Link>
                    <Link href={opposeUrl} color="red.300" _hover={{color: 'red.500'}}>
                      <BiDownvote size={20} style={{fill: myVote?.stance === 'oppose' ? 'currentColor' : undefined}} />
                    </Link>
                  </HStack>
                  {myVote && <Text fontSize="xs" color="gray.500">${myVote.amountUsd.toFixed(2)}</Text>}
                </Td>
              </Tr>
            </Fragment>
          )
        })}
        {entries.length === 0 && (
          <>
            <Tr>
              <Td colSpan={colCount} p={0} borderBottom="none">
                <Flex align="center">
                  <Divider flex={1} />
                  <Link href={emptyBuyUrl} color="gray.400" fontSize="xs" px={2} _hover={{color: 'blue.500'}}>
                    buy this spot for $1.00
                  </Link>
                  <Divider flex={1} />
                </Flex>
              </Td>
            </Tr>
            <Tr>
              <Td colSpan={colCount} textAlign="center" py={4} color="gray.500">
                No entries yet.
              </Td>
            </Tr>
          </>
        )}
      </Tbody>
    </Table>
  )
}
